import { useEffect, useReducer, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { CartContext, type CartContextValue } from './CartContext'
import { cartReducer, initialCartState } from './reducer'
import { loadCart, saveCart } from './storage'

export function CartProvider({ children }: { children: ReactNode }) {
  const { slug } = useParams<{ slug: string }>()
  // CartProvider doesn't remount when the route's :slug param changes alone
  // (it wraps the same matched <Route> element) — `key={slug}` forces a full
  // remount of CartProviderInner whenever the slug changes, so the reducer's
  // lazy initializer re-reads sessionStorage for the new slug instead of
  // carrying over the previous slug's state. This is React's documented
  // pattern for resetting state when a prop changes (react.dev "Resetting
  // state with a key").
  return (
    <CartProviderInner key={slug} slug={slug}>
      {children}
    </CartProviderInner>
  )
}

function CartProviderInner({ slug, children }: { slug: string | undefined; children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, initialCartState, () =>
    slug ? loadCart(slug) : initialCartState,
  )

  useEffect(() => {
    if (slug) saveCart(slug, state)
  }, [slug, state])

  const lineCount = state.lines.reduce((sum, line) => sum + line.quantity, 0)
  const totalCents = state.lines.reduce(
    (sum, line) => sum + line.unitPriceCents * line.quantity,
    0,
  )

  const value: CartContextValue = {
    lines: state.lines,
    lineCount,
    totalCents,
    addItem: (item, quantity = 1) => dispatch({ type: 'ADD_ITEM', item, quantity }),
    setQuantity: (menuItemId, quantity) => dispatch({ type: 'SET_QUANTITY', menuItemId, quantity }),
    removeItem: (menuItemId) => dispatch({ type: 'REMOVE_ITEM', menuItemId }),
    clear: () => dispatch({ type: 'CLEAR' }),
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}
